import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ActivityLogEntry,
  ActivityType,
  AdminStats,
  AdminUserSummary,
  Paginated,
} from '@app/common';
import { getStats, listActivity, listUsers } from '../api/admin.api';
import { SessionExpiredError } from '../api';
import { RequestCache } from '../lib/requestCache';
import {
  ADMIN_ACTIVITY_CACHE_TTL_MS,
  ADMIN_STATS_CACHE_TTL_MS,
  ADMIN_USERS_CACHE_TTL_MS,
} from '../constants/cache';

const PAGE_SIZE = 25;

// One cache per request kind. Module-level so every mount of a tab reads the same
// entries — a cache scoped to a component would be thrown away on every tab switch,
// which is exactly when it would have paid off.
const usersCache = new RequestCache<Paginated<AdminUserSummary>>(
  'admin:users',
  ADMIN_USERS_CACHE_TTL_MS
);
const activityCache = new RequestCache<Paginated<ActivityLogEntry>>(
  'admin:activity',
  ADMIN_ACTIVITY_CACHE_TTL_MS
);
const statsCache = new RequestCache<AdminStats>('admin:stats', ADMIN_STATS_CACHE_TTL_MS);

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface KeyedState<T> extends AsyncState<T> {
  /** The key this state describes, so a key change can be detected during render. */
  key: string;
  /** True when `data` came from the cache and no request is needed. */
  served: boolean;
}

/**
 * State to start a key with: a cache hit is already the answer, a miss shows the
 * loader over whatever was on screen before.
 */
function startState<T>(key: string, cache: RequestCache<T>, previous: T | null): KeyedState<T> {
  const hit = cache.get(key);
  return hit
    ? { key, data: hit, loading: false, error: null, served: true }
    : { key, data: previous, loading: true, error: null, served: false };
}

/**
 * Kept separate from `useDashboardData` on purpose: these hooks are only mounted
 * by the admin tabs, so an ordinary user's dashboard never issues an admin
 * request and never sees a 403 it would have to explain away.
 */
function useAsync<T>(
  load: () => Promise<T>,
  /**
   * Everything the request depends on, flattened into one string. A plain array
   * would have to be spread into the dependency list, and React requires that
   * list to keep a constant size between renders. It doubles as the cache key,
   * which is why it has to contain every input `load` reads.
   */
  key: string,
  cache: RequestCache<T>
): AsyncState<T> {
  const [state, setState] = useState<KeyedState<T>>(() => startState(key, cache, null));

  // Reading the cache during render rather than from the effect below. Effects run
  // after paint, so a hit applied there would still flash the loading row for a
  // frame — and that flash is the delay this cache exists to remove. React discards
  // this render and immediately re-runs it with the new state.
  if (state.key !== key) setState(startState(key, cache, state.data));

  // `load` is a fresh closure every render, so it is deliberately not a
  // dependency — `key` is what decides when to refetch.
  const loadRef = useRef(load);
  loadRef.current = load;

  // The effect must not depend on `served` (it would re-run when a fetch resolves
  // and flips it), so it reads the current value through a ref instead.
  const servedRef = useRef(state.served);
  servedRef.current = state.served;

  useEffect(() => {
    if (servedRef.current) return;

    let cancelled = false;

    cache
      .load(key, () => loadRef.current())
      .then((data) => {
        if (!cancelled) setState({ key, data, loading: false, error: null, served: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The session-expired dialog already covers the screen in this case, so
        // an inline error underneath it would just be noise.
        if (err instanceof SessionExpiredError) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }
        const message = err instanceof Error ? err.message : 'Request failed';
        setState({ key, data: null, loading: false, error: message, served: false });
      });

    return () => {
      cancelled = true;
    };
  }, [key, cache]);

  return { data: state.data, loading: state.loading, error: state.error };
}

/**
 * Adds a `reload` to the usual async state: banning a user changes a row this
 * list is already showing, and the table has to reflect it without a page
 * refresh. Bumping a nonce into the key is enough — `useAsync` refetches on any
 * key change, so there is no second code path to keep in step.
 */
export function useAdminUsers(page: number, search: string) {
  const [nonce, setNonce] = useState(0);

  const state = useAsync<Paginated<AdminUserSummary>>(
    () => listUsers({ page, pageSize: PAGE_SIZE, search: search || undefined }),
    `users:${page}:${search}:${nonce}`,
    usersCache
  );

  // A ban does not only change the row it was applied to — it can move a user
  // between pages of the roster — so the whole namespace goes, not one key. The
  // nonce then guarantees a key nothing has cached yet, even if a request that was
  // already in flight populates the old one in the meantime.
  const reload = useCallback(() => {
    usersCache.invalidate();
    setNonce((n) => n + 1);
  }, []);

  return { ...state, reload };
}

export function useAdminActivity(page: number, filters: { userId?: string; type?: ActivityType }) {
  return useAsync<Paginated<ActivityLogEntry>>(
    () =>
      listActivity({
        page,
        pageSize: PAGE_SIZE,
        userId: filters.userId,
        type: filters.type,
      }),
    `activity:${page}:${filters.userId ?? ''}:${filters.type ?? ''}`,
    activityCache
  );
}

export function useAdminStats(days: number) {
  return useAsync<AdminStats>(() => getStats(days), `stats:${days}`, statsCache);
}

export { PAGE_SIZE as ADMIN_PAGE_SIZE };
